import { useState } from "react";
import { Link } from "react-router";
import { ArrowRight, Check, Copy, Download, Eye, EyeOff, FileText, Info, Loader2 } from "lucide-react";

import { FilePreview } from "nazare-ui/components/preview";
import { useStoredAuth } from "nazare-ui/utils";

import {
  fetchWorkflowArtefact,
  fetchWorkflowArtefactContent,
  useRunArtefacts,
  type ArtefactSummary,
  type GetArtefactResponse,
} from "../../../../workflows-api";
import { formatTimestamp } from "./run-detail-format";
import styles from "./run-detail.module.css";

/**
 * Artefacts panel for a run — ported from nazare-frontend's
 * `screens/admin/workflows/artefacts-panel.tsx`. Lists a run's named outputs
 * (each an S3 object); previewable types (html/markdown/json/text) open in
 * an in-place viewer streamed through the BFF, every artefact can be
 * downloaded via a short-lived presigned URL, and each exposes a copyable
 * cross-job reference. The reference resolver at the foot of the panel
 * follows a pasted reference (from any run) back to its producing run.
 */

type ArtifactRef = { source: string; runId: string; name: string; stepId?: string };

/**
 * The portable reference to an artefact, used for cross-job lineage. Emits
 * the canonical `(runId, name, stepId?)` address the get-artefact endpoint
 * takes, plus the `source` World selector, as compact JSON — so arbitrary
 * names and step-ids (including ones containing `:` `/` `#`) round-trip
 * safely back through the lineage resolver.
 */
function artefactRef(a: { source: string; runId: string; stepId?: string | null; name: string }): string {
  return JSON.stringify({
    source: a.source,
    runId: a.runId,
    name: a.name,
    ...(a.stepId ? { stepId: a.stepId } : {}),
  });
}

/**
 * Content types (sans charset) FilePreview can render. A file artefact gets
 * an in-app preview only when its type is here; everything else is
 * download-only.
 */
const PREVIEWABLE_CONTENT_TYPES = new Set([
  "text/html",
  "text/markdown",
  "text/x-markdown",
  "application/json",
  "text/plain",
]);

function InlineArtefactPreview({
  text,
  name,
  contentType,
}: {
  text: string;
  name: string;
  contentType: string;
}) {
  return (
    <div className="rounded-sm border border-[var(--nzr-border)] overflow-auto max-h-[70vh]">
      <FilePreview contentType={contentType} text={text} title={name} />
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function CopyButton({ value, title }: { value: string; title: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={title}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-xs text-[12px] font-medium text-[var(--ink)] hover:bg-[var(--card-2)] border border-[var(--nzr-border)]"
    >
      {copied ? <Check size={13} className="text-[var(--green)]" /> : <Copy size={13} />}
      {copied ? "Copied" : "Copy ref"}
    </button>
  );
}

function ArtefactRow({ artefact, source }: { artefact: ArtefactSummary; source: string }) {
  const auth = useStoredAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Strip any `; charset=…` parameter so the type matches the preview registry.
  const baseType = (artefact.contentType ?? "").split(";")[0]?.trim() ?? "";
  const canPreview = PREVIEWABLE_CONTENT_TYPES.has(baseType);
  const ref = artefactRef({ source, runId: artefact.runId, stepId: artefact.stepId, name: artefact.name });

  // Load the artefact's text via the BFF content proxy (the BFF reads the S3
  // object server-side), so previews need no browser-to-S3 fetch.
  const loadText = async (): Promise<string | null> => {
    if (text !== null) return text;
    setLoading(true);
    setError(null);
    try {
      const t = await fetchWorkflowArtefactContent(
        auth,
        artefact.runId,
        artefact.name,
        source,
        artefact.stepId ?? undefined,
      );
      setText(t);
      return t;
    } catch {
      setError("Failed to load artefact.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onView = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    await loadText();
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetchWorkflowArtefact(
        auth,
        artefact.runId,
        artefact.name,
        source,
        artefact.stepId ?? undefined,
      );
      if (res.content.kind === "s3") {
        window.open(res.content.downloadUrl, "_blank", "noopener,noreferrer");
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="border-t border-[var(--border-soft)] first:border-t-0">
      <div className="flex items-center gap-3 py-2 px-3">
        <FileText size={15} className="text-[var(--nzr-muted)] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-mono text-[var(--ink)] truncate">{artefact.name}</span>
          </div>
          <div className="text-[11px] text-[var(--nzr-muted)] flex items-center gap-2 flex-wrap">
            <span>{artefact.contentType}</span>
            <span>·</span>
            <span className="tabular-nums">{formatBytes(artefact.size)}</span>
            {artefact.stepId && (
              <>
                <span>·</span>
                <span className="font-mono truncate">step {artefact.stepId}</span>
              </>
            )}
            <span>·</span>
            <span>{formatTimestamp(artefact.createdAt)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {canPreview && (
            <button
              type="button"
              onClick={() => void onView()}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-xs text-[12px] font-medium text-[var(--ink)] hover:bg-[var(--card-2)] border border-[var(--nzr-border)]"
            >
              {open ? <EyeOff size={13} /> : <Eye size={13} />}
              {open ? "Hide" : "View"}
            </button>
          )}
          <button
            type="button"
            onClick={() => void onDownload()}
            disabled={downloading}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-xs text-[12px] font-medium text-[var(--ink)] hover:bg-[var(--card-2)] border border-[var(--nzr-border)] disabled:opacity-50"
          >
            {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Download
          </button>
          <CopyButton value={ref} title={`Copy reference: ${ref}`} />
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3">
          {loading && <div className="text-[12px] text-[var(--nzr-muted)] py-2">Loading artefact…</div>}
          {error && <div className="text-[12px] text-[var(--red)] py-2">{error}</div>}
          {text !== null && <InlineArtefactPreview text={text} name={artefact.name} contentType={baseType} />}
        </div>
      )}
    </div>
  );
}

/** Parse a pasted artefact reference. Malformed JSON or a shape mismatch returns null. */
function parseRef(raw: string): ArtifactRef | null {
  let json: unknown;
  try {
    json = JSON.parse(raw.trim());
  } catch {
    return null;
  }
  if (!json || typeof json !== "object") return null;
  const obj = json as Record<string, unknown>;
  if (typeof obj.runId !== "string" || typeof obj.name !== "string") return null;
  return {
    runId: obj.runId,
    name: obj.name,
    source: typeof obj.source === "string" ? obj.source : "aws",
    stepId: typeof obj.stepId === "string" ? obj.stepId : undefined,
  };
}

function runHref(runId: string, source: string): string {
  const base = `/workflows/runs/${encodeURIComponent(runId)}`;
  return source === "aws" ? base : `${base}?source=${encodeURIComponent(source)}`;
}

/**
 * Follow a pasted cross-job artefact reference to its producing run and
 * content. The artefacts API addresses an artefact by `(source, runId,
 * stepId?, name)` but exposes no reverse "who consumed this" link, so
 * lineage is by-reference: copy a row's ref, paste it here (even from
 * another run) to resolve it.
 */
function ReferenceResolver({ source }: { source: string }) {
  const auth = useStoredAuth();
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<GetArtefactResponse | null>(null);
  const [resolvedText, setResolvedText] = useState<string | null>(null);

  const resolve = async () => {
    const parsed = parseRef(input);
    if (!parsed) {
      setError('Not a valid reference. Expected JSON: {"source","runId","name","stepId"?}');
      setResolved(null);
      setResolvedText(null);
      return;
    }
    setLoading(true);
    setError(null);
    setResolved(null);
    setResolvedText(null);
    try {
      const res = await fetchWorkflowArtefact(auth, parsed.runId, parsed.name, parsed.source, parsed.stepId);
      setResolved(res);
      const bt = (res.artifact.contentType ?? "").split(";")[0]?.trim() ?? "";
      if (PREVIEWABLE_CONTENT_TYPES.has(bt)) {
        setResolvedText(
          await fetchWorkflowArtefactContent(auth, parsed.runId, parsed.name, parsed.source, parsed.stepId),
        );
      }
    } catch {
      setError("Could not resolve that reference (artefact not found).");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={`${styles.surface} p-4 flex flex-col gap-3`}>
      <h2 className="text-[16px] font-semibold text-[var(--ink)]">Resolve a reference</h2>
      <div className="rounded-sm bg-[var(--blue-bg)] border border-[var(--blue)] px-3 py-2 flex items-start gap-2 text-[12px] text-[var(--blue)]">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          The read-only artefacts API exposes a run&apos;s produced artefacts and their addresses, but not
          which downstream run/step later consumed them. Paste a cross-job reference (copied from any run
          above) to follow it to its producing run and content.
        </span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void resolve()}
          placeholder='{"source":"aws","runId":"run-id","name":"artefact-name"}'
          className="flex-1 text-[13px] font-mono border border-[var(--nzr-border)] rounded-xs px-2.5 py-1.5 bg-[var(--nzr-card)] text-[var(--ink)] placeholder:text-[var(--nzr-muted)]"
        />
        <button
          type="button"
          onClick={() => void resolve()}
          disabled={loading || input.trim() === ""}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xs text-[13px] font-semibold bg-[var(--ink)] text-[var(--nzr-card)] hover:bg-[var(--card-hover)] disabled:opacity-50"
        >
          <ArrowRight size={14} />
          Resolve
        </button>
      </div>
      {error && <div className="text-[12px] text-[var(--red)]">{error}</div>}
      {resolved && (
        <div className="rounded-sm border border-[var(--nzr-border)] p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-mono text-[var(--ink)]">{resolved.artifact.name}</span>
            <Link
              to={runHref(resolved.artifact.runId, resolved.source)}
              className="text-[12px] font-semibold text-[var(--blue)] hover:underline"
            >
              open producing run →
            </Link>
          </div>
          {resolvedText !== null ? (
            <InlineArtefactPreview
              text={resolvedText}
              name={resolved.artifact.name}
              contentType={(resolved.artifact.contentType ?? "").split(";")[0]?.trim() ?? ""}
            />
          ) : resolved.content.kind === "s3" ? (
            <a
              href={resolved.content.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] font-semibold text-[var(--blue)] hover:underline"
            >
              download file artefact →
            </a>
          ) : null}
        </div>
      )}
    </section>
  );
}

function ArtefactsList({ runId, source }: { runId: string; source: string }) {
  const query = useRunArtefacts(runId, source);

  if (query.isLoading) {
    return <div className={`${styles.surface} p-4 text-[13px] text-[var(--nzr-muted)]`}>Loading artefacts…</div>;
  }
  if (query.isError) {
    return <div className={`${styles.surface} p-4 text-[13px] text-[var(--red)]`}>Failed to load artefacts.</div>;
  }
  const artefacts = query.data?.data ?? [];
  if (artefacts.length === 0) {
    return (
      <section className={`${styles.surface} p-4 text-[13px] text-[var(--nzr-muted)]`}>
        No artefacts recorded for this run.
      </section>
    );
  }
  return (
    <section className={styles.surface}>
      {query.data?.hasMore && (
        <div className="px-3 py-2 text-[11px] text-[var(--orange)] bg-[var(--orange-bg)] border-b border-[var(--orange)] rounded-t-sm">
          Artefact list truncated — more exist than are shown.
        </div>
      )}
      {artefacts.map((a) => (
        <ArtefactRow key={a.artifactId} artefact={a} source={source} />
      ))}
    </section>
  );
}

/** The Artefacts tab: a run's produced artefacts, followed by the cross-job lineage resolver. */
export function ArtefactsPanel({ runId, source }: { runId: string; source: string }) {
  return (
    <>
      <ArtefactsList runId={runId} source={source} />
      <ReferenceResolver source={source} />
    </>
  );
}
